import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { Image, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { RRule } from 'rrule';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, ButtonGroup, CardView, Spacer } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColorScheme from 'hooks/useColorScheme';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { selectShouldShowAutosaveSelectPeriod } from 'features/pots/reducers/selectors';
import createStyleSheets from 'utils/createStyleSheets';
const autoSaveSavingsImg = require('../../img/autosave_growth_graphics.png');
const autoSaveSavingsDarkImg = require('../../img/autosave_growth_graphics_dark.png');
const AutosaveSaveMoneyIntro = withAnimatedNavigationHeader(({ navigation, scrollHandler }) => {
    const colorScheme = useColorScheme();
    const styles = useStyles(styleSet);
    const shouldShowAutosaveSelectPeriod = useAppSelector(selectShouldShowAutosaveSelectPeriod);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    const imgSource = useMemo(() => {
        if (colorScheme === 'dark') {
            return autoSaveSavingsDarkImg;
        }
        return autoSaveSavingsImg;
    }, [colorScheme]);
    const handleContinue = useCallback(() => {
        if (shouldShowAutosaveSelectPeriod) {
            navigation.navigateDeprecated('AutosaveChoosePeriod');
        }
        else {
            navigation.navigateDeprecated('AutosaveChooseSavingRules', {
                collectionFrequency: {
                    rrule: new RRule({
                        freq: RRule.WEEKLY,
                        byweekday: RRule.WE,
                    }).toString(),
                },
            });
        }
    }, [shouldShowAutosaveSelectPeriod]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Let’s set up Autosave" subTitle="Autosavers save 10x more than manual savers"/>
          <CardView withPadding noMarginTop noMarginBottom>
            <View style={styles.badge}>
              <Text Text-16 OnBrand>
                Money saved
              </Text>
            </View>
            <Image source={imgSource} style={styles.img}/>
            <Spacer h={12}/>
            <View style={styles.row}>
              <View style={styles.flexEnd}>
                <Text Text-14>Without Autosave</Text>
              </View>
              <View style={styles.flex}>
                <Text Text-14>With Autosave</Text>
              </View>
            </View>
          </CardView>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button brand title="Continue" onPress={handleContinue}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    row: {
        gap: rem(16),
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    flexEnd: {
        flex: 1,
        alignItems: 'flex-end',
    },
    img: {
        width: '100%',
        aspectRatio: 2.04,
        alignSelf: 'center',
    },
    badge: {
        zIndex: 1,
        top: rem(16),
        left: rem(16),
        position: 'absolute',
        borderRadius: rem(8),
        paddingVertical: rem(8),
        paddingHorizontal: rem(16),
        backgroundColor: colors.buttons.brandGroup,
    },
    paragraph: {
        marginHorizontal: rem(16),
    },
}));
export default AutosaveSaveMoneyIntro;
